import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ZONE, findSkinImage, fmt, rise,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneSelect, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import {
  MARKETPLACES, DIRECTIONS, fetchMarketHistory, marketLabel, summarize, vsReference,
  isCompleted, stateLabel,
} from './marketHistory';
import { StickerStrip } from './historyShared';
import { wearClass } from './helpers';
import './HistoryTab.css';

// History Zone — the marketplace ledger rebuilt on the Overview 7 board.
// Same rows and same figures as HistoryTab.jsx, printed on paper.

const SORTS = [
  { key: 'date', label: 'Newest first' },
  { key: 'old',  label: 'Oldest first' },
  { key: 'high', label: 'Highest value' },
  { key: 'low',  label: 'Lowest value' },
];

const dateOf = iso => new Date(iso);

export default function HistoryTabZone({ skinsData = [] }) {
  const [market, setMarket]       = useState('csfloat');
  const [direction, setDirection] = useState('sale');
  const [query, setQuery]         = useState('');
  const [sort, setSort]           = useState('date');
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchMarketHistory(market)
      .then(list => { if (live) { setEntries(list); setLoading(false); } })
      .catch(() => { if (live) { setEntries([]); setLoading(false); } });
    return () => { live = false; };
  }, [market]);

  const sideEntries = useMemo(
    () => entries.filter(e => e.direction === direction),
    [entries, direction]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? sideEntries.filter(e => e.skin.toLowerCase().includes(q) || (e.counterparty || '').toLowerCase().includes(q))
      : sideEntries;
    const by = {
      date: (a, b) => dateOf(b.date) - dateOf(a.date),
      old:  (a, b) => dateOf(a.date) - dateOf(b.date),
      high: (a, b) => b.price - a.price,
      low:  (a, b) => a.price - b.price,
    }[sort];
    return [...list].sort(by);
  }, [sideEntries, query, sort]);

  const totals = useMemo(() => summarize(sideEntries), [sideEntries]);
  const shown  = useMemo(() => summarize(filtered), [filtered]);

  const isSale = direction === 'sale';

  return (
    <div className="hst-root zone zn-root">

      <ZoneHead
        pre="THE" hl="PAPER" post="TRAIL"
        sub="Every buy and sale straight from the marketplace itself. Pick a market, then flip between what you sold and what you bought."
        stickers={[
          { text: marketLabel(market), bg: ZONE.teal },
          { text: `${totals.count} ${isSale ? 'sales' : 'buys'} on record`, bg: ZONE.yellow },
          ...(totals.cancelled > 0
            ? [{ text: `${totals.cancelled} never completed`, bg: ZONE.pink }]
            : []),
        ]}
      />

      <ZoneStats items={[
        { label: isSale ? 'Gross sales' : 'Total spent', value: `$${fmt(totals.gross)}`,
          sub: `${totals.count} ${isSale ? 'sale' : 'purchase'}${totals.count !== 1 ? 's' : ''} on ${marketLabel(market)}`,
          bg: isSale ? ZONE.lime : ZONE.purple },
        { label: isSale ? 'Net after fees' : 'Average price',
          value: `$${fmt(isSale ? totals.net : totals.avg)}`,
          sub: isSale ? `$${fmt(totals.fees)} paid in marketplace fees` : 'per item bought',
          bg: ZONE.white },
        { label: `Largest ${isSale ? 'sale' : 'buy'}`,
          value: totals.largest ? `$${fmt(totals.largest.price)}` : 'n/a',
          sub: totals.largest ? totals.largest.skin : 'nothing recorded yet',
          bg: ZONE.yellow },
      ]} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <div className="hst-market">
          <span className="hst-market-label">Market</span>
          <select className="zn-select" value={market} onChange={e => setMarket(e.target.value)} aria-label="Marketplace">
            {MARKETPLACES.map(m => (
              <option key={m.id} value={m.id} disabled={!m.ready}>
                {m.ready ? m.label : `${m.label} (soon)`}
              </option>
            ))}
          </select>
        </div>

        <ZoneChips options={DIRECTIONS} value={direction} onChange={setDirection} />

        <ZoneSearch value={query} onChange={setQuery} placeholder="Search skin or trader…" />

        {/* Count and money must describe the same rows: the total excludes
            trades that never completed, so the count does too. */}
        <span className="zn-count" style={{ marginLeft: 'auto' }}>
          {shown.count} {isSale ? 'sale' : 'buy'}{shown.count !== 1 ? 's' : ''} · ${fmt(shown.gross)}
          {shown.cancelled > 0 && ` · ${shown.cancelled} cancelled`}
        </span>

        <ZoneSelect value={sort} onChange={setSort} options={SORTS} label="Sort history" />
      </div>

      {/* ── Ledger ─────────────────────────────────── */}
      {loading ? (
        <ZoneEmpty>Loading {marketLabel(market)} history…</ZoneEmpty>
      ) : filtered.length === 0 ? (
        <ZoneEmpty>
          {sideEntries.length === 0
            ? `No ${isSale ? 'sales' : 'purchases'} recorded on ${marketLabel(market)} yet.`
            : 'No trades match your search.'}
        </ZoneEmpty>
      ) : (
        <div className="hst-ledger">
          <div className="hst-head-row">
            <span>Date</span>
            <span>Item</span>
            <span>Float</span>
            <span>{isSale ? 'Buyer' : 'Seller'}</span>
            <span>vs market</span>
            <span style={{ textAlign: 'right' }}>{isSale ? 'Sold for' : 'Paid'}</span>
          </div>

          {filtered.map((e, i) => {
            const img   = findSkinImage(skinsData, e.skin, e.condition);
            const ref   = vsReference(e);
            const ts    = dateOf(e.date);
            const dead  = !isCompleted(e);
            const label = stateLabel(e);
            return (
              <motion.div key={e.id} className={`hst-row ${e.direction}${dead ? ' dead' : ''}`} {...rise(i)}>
                <div className="hst-date">
                  {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  <span className="t">{ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                </div>

                <div className="hst-item">
                  {img ? <img src={img} alt="" loading="lazy" /> : <div className="hst-thumb-blank" />}
                  <div style={{ minWidth: 0 }}>
                    <div className="hst-name" title={e.skin}>{e.skin}</div>
                    <div className="hst-meta">
                      {label && <span className={`hst-state ${e.state}`}>{label}</span>}
                      <span className={`hst-wear ${wearClass(e.condition)}`}>{e.condition || 'n/a'}</span>
                      {e.paintSeed != null && <span>seed {e.paintSeed}</span>}
                    </div>
                  </div>
                </div>

                <div className="hst-num ink">{e.float != null ? e.float.toFixed(4) : 'n/a'}</div>

                <div className="hst-num">{e.counterparty || 'n/a'}</div>

                {ref ? (
                  <div className={`hst-ref ${ref.diff >= 0 ? 'win' : 'lose'}`}>
                    {ref.diff >= 0 ? '+' : '−'}${fmt(ref.diff)}
                    <span className="p">{ref.pct >= 0 ? '+' : '−'}{Math.abs(ref.pct).toFixed(1)}% vs ref</span>
                  </div>
                ) : <div className="hst-num">n/a</div>}

                <div className="hst-money">
                  <div className="v">${fmt(e.price)}</div>
                  <div className="s">
                    {dead
                      ? 'no money moved'
                      : isSale ? `net $${fmt(e.net)} after $${fmt(e.fee)} fee` : 'no buyer fee'}
                  </div>
                </div>

                <StickerStrip stickers={e.stickers} skinsData={skinsData} />
              </motion.div>
            );
          })}
        </div>
      )}

      <ZoneFooter stickers={[
        { text: 'captured from the marketplace by the browser extension', bg: ZONE.pink },
        { text: `${marketLabel(market)} · ${isSale ? 'sold' : 'purchased'}`, bg: ZONE.teal, right: true },
      ]} />
    </div>
  );
}
